import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { useQueryResourceAncestors } from '../../../resource-detail/queries/useQueryResourceAncestors.js';
import { LocationSelector } from './LocationSelector.js';
import { ancestorToLocationSelectorNodePathItem, rootToLocationSelectorNodeData, folderToLocationSelectorNodeData } from './utils.js';
import { ModalFolderCreate } from '../ModalCreateFolder/ModalCreateFolder.js';
import { MoveModalNoteStack } from '../ModalMoveResources/NotestackContent.js';
import { ResourcePath } from '../ResourcePath/ResourcePath.js';
import { useQueryResourceList } from '../../queries/useQueryResourceList.js';
import { useQuerySpaceOrFolderById } from '../../queries/useQuerySpaceOrFolderById.js';
import { ModalSpaceCreate } from '../../../spaces/components/ModalSpaceCreate/ModalSpaceCreate.js';
import { DialogTitle, DialogDescription } from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import React__default, { useMemo } from 'react';
import { styled } from 'styled-components';
import { getDefaultRadixPortalContainer } from '@fabric-app/compat';
import { Modal } from '@fabric-app/ui/components/Modal/Modal';
import { mediaNotMobile } from '@fabric-app/ui/constants/styled-utils';
import { Flex } from '@fabric-app/ui/components/Flex';
import { Button, ButtonIcon } from '@fabric-app/ui/components/Button';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';
import { useBoolState } from '@fabric-app/hooks/useBooleanState';
import { useInputControls } from '@fabric-app/hooks/useInputControls';
import { useDebounce } from '@fabric-app/hooks/useDebounce';
import { TextInput } from '@fabric-app/ui/components/TextInput/TextInput';
import { Spinner } from '@fabric-app/ui/components/Spinner';
import { ScrollArea } from '@fabric-app/ui/components/ScrollArea';
import { useObjectState } from '@fabric-app/hooks/useObjectState';
import { ModalDrawerContent } from '@fabric-app/ui/components/Modal/Drawer';
import { CloseIcon, SearchIcon } from '@fabric-app/icons/icons';
import { isInFabricMobilePlatform } from '@fabric-app/compat/capabilities/platform';
import { IconSidebar24Spaces } from '@fabric-app/icons/svg';

const LocationSelectorContent = styled(Modal.Content).withConfig({
  displayName: "ModalDestinationSelector__LocationSelectorContent",
  componentId: "sc-4771daf-0"
})([
  `gap:0;max-width:none;`,
  `{--modal-transform-x:0%;--modal-transform-y:calc(-50% - 0.5px);left:auto;right:2rem;height:100%;max-height:calc(100dvh - 4rem);width:calc(50% - 2rem);min-width:300px;}`
], mediaNotMobile);
const HeaderContent = styled(Flex).attrs({
  direction: "column"
}).withConfig({
  displayName: "ModalDestinationSelector__HeaderContent",
  componentId: "sc-4771daf-1"
})([
  `flex-shrink:0;`
]);
const StyledTitle = styled(Modal.Title).withConfig({
  displayName: "ModalDestinationSelector__StyledTitle",
  componentId: "sc-4771daf-2"
})([
  `margin-right:auto;`
]);
const ModalDestinationSelector = ({ resources, getIsNodeSelectable, onClose: _onClose, open, title = "Move to...", onDestinationSelect, ctaButtonProps: ctaProps, initialDestinationId, disableDrawer = false, autoFocusSearchInput = false }) => {
  const { isMobileView } = useResponsive();
  const openStateCreateFolder = useBoolState(false);
  const openStateCreateSpace = useBoolState(false);
  const selectedNodeState = useObjectState(null);
  const { value: selectedNode } = selectedNodeState;
  const scrollAreaRef = React__default.useRef(null);
  const { accessibleAncestors } = useQueryResourceAncestors(selectedNode?.id, {
    enabled: open
  });
  const selectedNodePath = useMemo(() => {
    if (!selectedNode) return [];
    return [
      ...accessibleAncestors.map(ancestorToLocationSelectorNodePathItem),
      {
        id: selectedNode.id,
        title: selectedNode.title
      }
    ];
  }, [
    accessibleAncestors,
    selectedNode
  ]);
  const setSelectedNode = (node) => {
    selectedNodeState.set(node);
  };
  const initialDestination = useQuerySpaceOrFolderById(initialDestinationId || "");
  React__default.useEffect(() => {
    if (!initialDestination || !open) {
      return;
    }
    if (initialDestination.type === "space") {
      setSelectedNode(rootToLocationSelectorNodeData(initialDestination.data));
    }
    if (initialDestination.type === "folder") {
      setSelectedNode(folderToLocationSelectorNodeData(initialDestination.data));
    }
  }, [
    open
  ]);
  const onClose = () => {
    _onClose();
    selectedNodeState.clear();
    searchKeywordInputControls.clear();
  };
  const searchKeywordInputControls = useInputControls("");
  const debouncedSearchKeyword = useDebounce(searchKeywordInputControls.value, 200);
  const queryResourceFolders = useQueryResourceList({
    kind: [
      "folder"
    ],
    name: debouncedSearchKeyword
  }, {
    enabled: debouncedSearchKeyword.length > 0
  });
  const ctaButtonProps = {
    ...ctaProps,
    onClick: (e) => {
      ctaProps?.onClick?.(e);
      selectedNodeState.value && onDestinationSelect(selectedNodeState.value);
      onClose();
    }
  };
  const content = /* @__PURE__ */ jsxs(Fragment, {
    children: [
      /* @__PURE__ */ jsxs(VisuallyHidden, {
        children: [
          /* @__PURE__ */ jsx(DialogTitle, {
            children: title
          }),
          /* @__PURE__ */ jsx(DialogDescription, {
            children: "Select destination folder or create a new one"
          })
        ]
      }),
      /* @__PURE__ */ jsx(Modal.Body, {
        style: {
          gap: "1.25rem",
          flexShrink: 0,
          paddingBottom: 0
        },
        background: "transparent",
        children: /* @__PURE__ */ jsxs(HeaderContent, {
          gap: "sectionsShort",
          style: {
            paddingBottom: 0
          },
          children: [
            /* @__PURE__ */ jsxs(Flex, {
              alignItems: "center",
              gap: "sectionsShort",
              justifyContent: "flex-start",
              style: {
                paddingLeft: "1rem"
              },
              children: [
                /* @__PURE__ */ jsx(StyledTitle, {
                  size: "md-shared",
                  children: title
                }),
                selectedNode ? /* @__PURE__ */ jsx(Button, {
                  variant: "primary-inverse",
                  onClick: openStateCreateFolder.handleTrue,
                  children: "Create subfolder"
                }) : /* @__PURE__ */ jsx(Button, {
                  variant: "primary-inverse",
                  onClick: openStateCreateSpace.handleTrue,
                  "data-testid": "move-items-create-space-button",
                  children: "Create space"
                }),
                isMobileView && isInFabricMobilePlatform() && /* @__PURE__ */ jsx(Modal.Close, {
                  noAutoMargin: true
                })
              ]
            }),
            /* @__PURE__ */ jsx(TextInput, {
              ...searchKeywordInputControls,
              autoFocus: autoFocusSearchInput,
              inputHeight: "lg",
              startAdornment: /* @__PURE__ */ jsx(SearchIcon, {}),
              endAdornment: /* @__PURE__ */ jsxs(Flex, {
                alignItems: "center",
                gap: "element",
                children: [
                  queryResourceFolders.isFetching && /* @__PURE__ */ jsx(Spinner, {
                    size: 18
                  }),
                  searchKeywordInputControls.value && /* @__PURE__ */ jsx(ButtonIcon, {
                    onClick: searchKeywordInputControls.clear,
                    style: {
                      marginRight: -6
                    },
                    children: /* @__PURE__ */ jsx(CloseIcon, {})
                  })
                ]
              }),
              placeholder: "Search for a folder or space"
            })
          ]
        })
      }),
      /* @__PURE__ */ jsx(ScrollArea.Default, {
        ref: scrollAreaRef,
        innerDisplayFlex: true,
        style: {
          height: "100%"
        },
        children: /* @__PURE__ */ jsx(Modal.Body, {
          style: {
            gap: "1.25rem",
            height: "100%"
          },
          background: "transparent",
          children: /* @__PURE__ */ jsx(LocationSelector, {
            searchKeyword: searchKeywordInputControls.value,
            activeNodePath: selectedNodePath || [],
            selectedNode: selectedNodeState.value,
            scrollAreaEl: scrollAreaRef.current,
            getIsNodeSelectable,
            querySearchResources: queryResourceFolders,
            onSelectNode: setSelectedNode
          })
        })
      })
    ]
  });
  return /* @__PURE__ */ jsxs(Fragment, {
    children: [
      /* @__PURE__ */ jsx(Modal.Root, {
        open,
        onOpenChange: onClose,
        children: /* @__PURE__ */ jsx(Modal.Portal, {
          container: getDefaultRadixPortalContainer(),
          children: /* @__PURE__ */ jsxs(Modal.Overlay, {
            visibleOnMobileViewport: true,
            children: [
              isMobileView && !disableDrawer ? /* @__PURE__ */ jsxs(ModalDrawerContent, {
                onClose,
                "aria-describedby": "Select target space or folder destination",
                deprecatedPadding: true,
                children: [
                  content,
                  /* @__PURE__ */ jsx(Modal.Footer, {
                    variant: "call-to-action",
                    style: {
                      marginTop: "auto",
                      padding: 16
                    },
                    children: /* @__PURE__ */ jsxs(Modal.DoubleButtonContainer, {
                      style: {
                        width: "100%"
                      },
                      children: [
                        /* @__PURE__ */ jsx(Modal.Button, {
                          variant: "bg-secondary",
                          onClick: onClose,
                          style: {
                            marginRight: "auto"
                          },
                          children: "Cancel"
                        }),
                        /* @__PURE__ */ jsx(Modal.Button, {
                          disabled: !selectedNode,
                          type: "submit",
                          "data-testid": "move-items-modal-move-button",
                          ...ctaButtonProps
                        })
                      ]
                    })
                  })
                ]
              }) : /* @__PURE__ */ jsxs(LocationSelectorContent, {
                respectMobileKeyboard: true,
                "aria-describedby": "Select target space or folder destination",
                onEscapeKeyDown: (e) => {
                  if (openStateCreateSpace.value || openStateCreateFolder.value) {
                    e.preventDefault();
                  }
                },
                children: [
                  content,
                  /* @__PURE__ */ jsxs(Modal.Footer, {
                    variant: "call-to-action",
                    background: "primary",
                    style: {
                      marginTop: "auto"
                    },
                    children: [
                      /* @__PURE__ */ jsx(ResourcePath, {
                        path: selectedNodePath
                      }),
                      /* @__PURE__ */ jsx(Button, {
                        style: {
                          minWidth: 100
                        },
                        variant: "primary-inverse",
                        disabled: !selectedNode,
                        ...ctaButtonProps
                      })
                    ]
                  })
                ]
              }),
              /* @__PURE__ */ jsx(Modal.FloatingDesktopCloseButton, {}),
              resources && resources.length > 0 && /* @__PURE__ */ jsx(MoveModalNoteStack, {
                resources
              })
            ]
          })
        })
      }),
      /* @__PURE__ */ jsx(ModalFolderCreate, {
        modalProps: openStateCreateFolder.modalProps,
        parent: selectedNode ? {
          id: selectedNode.id,
          name: selectedNode.title
        } : void 0,
        onCreate: (resource) => {
          setSelectedNode(folderToLocationSelectorNodeData(resource));
          searchKeywordInputControls.clear();
        }
      }),
      /* @__PURE__ */ jsx(ModalSpaceCreate, {
        modalProps: openStateCreateSpace.modalProps,
        onCreate: (space) => {
          setSelectedNode({
            displayType: "space",
            id: space.id,
            title: space.name,
            canCreateChildren: true,
            isReadOnly: false,
            icon: /* @__PURE__ */ jsx(IconSidebar24Spaces, {})
          });
          searchKeywordInputControls.clear();
        }
      })
    ]
  });
};

export { ModalDestinationSelector };
//# sourceMappingURL=ModalDestinationSelector.js.map
